/**
 * Flat's Menu view: ONE level of the menu at a time. Entering a folder slides
 * to its contents, with "Back to <parent>" and the path above — to any depth,
 * because the level is just a node of the shared tree, never a fixed tier.
 *
 * The list is a keyboard list, not a tree: Up/Down/Home/End move, Enter opens
 * a screen or enters a folder, Right enters any container (a folder, or a
 * website's pages), Left / Backspace go back a level. Whenever the LIST moves
 * to another level, focus lands on that level's first entry; a level change
 * made from elsewhere (the breadcrumb, a screen opened from Pinned) leaves
 * focus where the person put it.
 */
import { ChevronLeft, ChevronRight, Star } from 'lucide-react'
import { useEffect, useRef, type KeyboardEvent } from 'react'
import { tw } from '../../../testHooks'
import { NavIcon } from '../../nav/navIcons'
import { useEntryMenu } from '../../nav/parts/ShellLists'
import { S } from '../../nav/tokens'
import type { NavTreeNode } from '../../nav/tree'
import type { ShellNavModel } from '../../nav/useShellNav'

export interface LevelViewProps {
  model: ShellNavModel
  appTitle: string
  /** The container on show — null is the top of the menu. */
  level: NavTreeNode | null
  /** Show another level (null = the top). */
  onLevel: (id: string | null) => void
  emptyNavMessage: string
}

function countText(nodes: NavTreeNode[]): string {
  const folders = nodes.filter((n) => n.isFolder).length
  const screens = nodes.length - folders
  const parts: string[] = []
  if (folders) parts.push(`${folders} ${folders === 1 ? 'folder' : 'folders'}`)
  if (screens) parts.push(`${screens} ${screens === 1 ? 'screen' : 'screens'}`)
  return parts.join(' · ')
}

function itemsText(n: number): string {
  if (!n) return 'Empty'
  return n === 1 ? '1 item' : `${n} items`
}

export function LevelView({ model, appTitle, level, onLevel, emptyNavMessage }: LevelViewProps) {
  const listRef = useRef<HTMLDivElement>(null)
  const entryMenu = useEntryMenu(model)
  // Set by a move the list itself made; the effect below then focuses the
  // first entry of the level that just rendered.
  const focusFirst = useRef(false)
  const levelId = level?.item.id ?? null
  useEffect(() => {
    if (!focusFirst.current) return
    focusFirst.current = false
    const first = listRef.current?.querySelector<HTMLElement>('[data-flat-entry]')
    ;(first ?? listRef.current?.querySelector<HTMLElement>('[data-flat-back]'))?.focus()
  }, [levelId])

  const entries = level ? level.children : model.roots
  const parent = level ? level.path[level.path.length - 1] ?? null : null
  const backName = parent ? parent.title : 'Menu'
  const go = (id: string | null) => { focusFirst.current = true; onLevel(id) }
  const back = () => { if (level) go(parent ? parent.id : null) }

  const currentId = model.current?.id ?? null
  // Roving tabindex: the open screen when it sits on this level, else the first.
  const focusId = entries.some((n) => n.item.id === currentId) ? currentId : entries[0]?.item.id

  const onKey = (e: KeyboardEvent<HTMLDivElement>) => {
    const els = [...(listRef.current?.querySelectorAll<HTMLElement>('[data-flat-entry]') || [])]
    const cur = document.activeElement as HTMLElement | null
    const i = cur ? els.indexOf(cur) : -1
    const move = (j: number) => { e.preventDefault(); els[Math.max(0, Math.min(els.length - 1, j))]?.focus() }
    switch (e.key) {
      case 'ArrowDown': if (i >= 0) move(i + 1); break
      case 'ArrowUp': if (i >= 0) move(i - 1); break
      case 'Home': if (i >= 0) move(0); break
      case 'End': if (i >= 0) move(els.length - 1); break
      case 'ArrowRight': {
        if (i < 0) break
        const node = model.tree.byId.get(cur?.dataset.nodeId || '')
        if (node?.isContainer) { e.preventDefault(); go(node.item.id) }
        break
      }
      case 'ArrowLeft':
      case 'Backspace':
        if (level && (i >= 0 || cur?.dataset.flatBack !== undefined)) { e.preventDefault(); back() }
        break
      default:
    }
  }

  const levelTitle = level ? level.item.title : 'Menu'
  const levelPath = level
    ? ['Menu', ...level.path.map((p) => p.title)].join(' › ')
    : [appTitle, countText(entries)].filter(Boolean).join(' · ')

  return (
    <div ref={listRef} onKeyDown={onKey} style={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}>
      <div style={{ padding: '16px 14px 10px', flexShrink: 0 }}>
        {level && (
          <button type="button" className="tw-sh-btn" data-flat-back="" onClick={back}
            aria-label={`Back to ${backName}`} title={`Back to ${backName}`}
            {...tw('action', 'shell-flat-back', { to: parent?.id || 'menu' })}
            style={{
              display: 'inline-flex', alignItems: 'center', gap: 4, maxWidth: '100%', height: 36,
              padding: '0 12px 0 6px', marginBottom: 8, border: 'none', borderRadius: S.radius,
              background: S.panel, color: S.text, fontSize: 14, fontWeight: 600, cursor: 'pointer',
              fontFamily: 'inherit',
            }}>
            <ChevronLeft size={17} aria-hidden style={{ flexShrink: 0 }} />
            <span className="tw-sh-clip">{backName}</span>
          </button>
        )}
        <h2 className="tw-sh-clip" style={{ margin: 0, fontSize: 20, fontWeight: 700, lineHeight: 1.25, color: S.text }}>
          {levelTitle}
        </h2>
        {levelPath && (
          <div className="tw-sh-clip" style={{ marginTop: 2, fontSize: 13.5, color: S.text2 }}>{levelPath}</div>
        )}
      </div>
      <div role="list" aria-label={level ? `${levelTitle} contents` : 'Menu'}
        style={{ flex: 1, minHeight: 0, overflow: 'auto', padding: '4px 14px 16px', display: 'flex', flexDirection: 'column', gap: 6 }}>
        {entries.length === 0 && (
          <p style={{ margin: '8px 4px', fontSize: 14, color: S.text2, lineHeight: 1.5 }}>
            {level ? 'No screens in this folder yet.' : emptyNavMessage}
          </p>
        )}
        {entries.map((n) => (
          <div key={n.item.id} role="listitem" style={{ display: 'flex', alignItems: 'stretch', gap: 4 }}>
            {n.isFolder
              ? <FolderEntry node={n} tabbable={n.item.id === focusId} onEnter={() => go(n.item.id)} />
              : <ScreenEntry node={n} model={model} tabbable={n.item.id === focusId} entryMenu={entryMenu}
                  onDrill={n.isContainer ? () => go(n.item.id) : undefined} />}
          </div>
        ))}
      </div>
      {entryMenu.ui}
    </div>
  )
}

/** A folder: a tile that slides to its contents. */
function FolderEntry({ node, tabbable, onEnter }: { node: NavTreeNode; tabbable: boolean; onEnter: () => void }) {
  const { item, children } = node
  return (
    <button
      type="button"
      className="tw-sh-item"
      data-flat-entry=""
      data-node-id={item.id}
      tabIndex={tabbable ? 0 : -1}
      onClick={onEnter}
      aria-label={`${item.title}, folder, ${itemsText(children.length)}`}
      {...tw('nav', item.obj_name || item.title, { type: item.obj_type, folder: true })}
      style={{
        flex: 1, minWidth: 0, display: 'flex', alignItems: 'center', gap: 12, minHeight: 64,
        padding: '10px 12px', border: `1px solid ${S.border}`, borderRadius: S.radius,
        background: S.panel, color: S.text, textAlign: 'left', cursor: 'pointer', fontFamily: 'inherit',
      }}
    >
      <span aria-hidden style={{
        width: 40, height: 40, flexShrink: 0, borderRadius: 9, background: S.accent, color: S.panel,
        display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
      }}>
        <NavIcon node={item} size={20} />
      </span>
      <span style={{ flex: 1, minWidth: 0 }}>
        <span className="tw-sh-clip" style={{ display: 'block', fontSize: 15, fontWeight: 600 }}>{item.title}</span>
        <span style={{ display: 'block', fontSize: 13, color: S.text2 }}>{itemsText(children.length)}</span>
      </span>
      <ChevronRight size={18} aria-hidden style={{ flexShrink: 0, color: S.text2 }} />
    </button>
  )
}

/** A screen. A website (X) — or anything else carrying children — also opens,
 *  and offers a separate control to show its pages. */
function ScreenEntry({ node, model, tabbable, entryMenu, onDrill }: {
  node: NavTreeNode
  model: ShellNavModel
  tabbable: boolean
  entryMenu: ReturnType<typeof useEntryMenu>
  onDrill?: () => void
}) {
  const { item } = node
  const selected = model.current?.id === item.id
  const pinned = model.isPinned(item)
  return (
    <>
      <button
        type="button"
        className="tw-sh-item"
        data-flat-entry=""
        data-node-id={item.id}
        tabIndex={tabbable ? 0 : -1}
        aria-current={selected ? 'page' : undefined}
        onClick={() => model.open(item)}
        onContextMenu={entryMenu.onContextMenu(item)}
        onKeyDown={entryMenu.onKeyDown(item)}
        {...tw('nav', item.obj_name || item.title, { type: item.obj_type, folder: false, selected })}
        style={{
          flex: 1, minWidth: 0, display: 'flex', alignItems: 'center', gap: 12, minHeight: 50,
          padding: '6px 10px', border: 'none', borderRadius: S.radius,
          background: selected ? S.accentWeak : 'transparent', color: S.text,
          textAlign: 'left', cursor: 'pointer', fontFamily: 'inherit',
        }}
      >
        <span aria-hidden style={{
          width: 32, height: 32, flexShrink: 0, borderRadius: 8,
          background: selected ? S.accent : S.accentWeak, color: selected ? S.panel : S.accent,
          display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
        }}>
          <NavIcon node={item} size={17} />
        </span>
        <span className="tw-sh-clip" style={{ flex: 1, minWidth: 0, fontSize: 15, fontWeight: selected ? 700 : 500 }}>
          {item.title}
        </span>
        {pinned && <Star size={13} aria-label="Pinned" role="img" fill={S.star} color={S.starStroke} style={{ flexShrink: 0 }} />}
      </button>
      {onDrill && (
        <button
          type="button"
          className="tw-sh-btn"
          onClick={onDrill}
          aria-label={`Show pages in ${item.title}`}
          title={`Show pages in ${item.title}`}
          {...tw('action', 'shell-flat-drill', { obj: item.obj_name, id: item.id })}
          style={{
            width: 44, flexShrink: 0, display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
            border: `1px solid ${S.border}`, borderRadius: S.radius, background: S.panel, color: S.text2,
            cursor: 'pointer', padding: 0, fontFamily: 'inherit',
          }}
        >
          <ChevronRight size={18} aria-hidden />
        </button>
      )}
    </>
  )
}
